import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { DrawerBody, DrawerModal } from '@doltech/ui/lib/figma/Mobile/Modal/DrawerModal';
import { SubText } from '@doltech/ui/lib/figma/Typography';
import { TestTypeEnum } from '@doltech/utils/lib/constants';
import { Tabs } from 'antd';
import cl from 'classnames';
import { range } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { useTestSubmissionStore } from '../../model/test-submissions.hook';
import {
  BodyWrapper,
  ButtonFooter,
  DivQuestionBox,
  OverallTitle,
  QuestionGroupList,
  QuestionList,
} from './OverallDrawerModal';

interface OverallDrawerModalProps {
  visible: boolean;
  onClose?: () => void;
  onSubmit?: () => void;
  goToQuestion?: (question: string, testSectionIndex: number, groupIndex: number) => void;
}

const noPaddingStyle = { padding: 0 };

export const ListeningOverallDrawerModal = observer(
  ({ goToQuestion, onSubmit, visible, onClose }: OverallDrawerModalProps) => {
    const store = useTestSubmissionStore();

    const isFullTest = store.testType === TestTypeEnum.FULL_TEST;

    const renderQuestionGroup = React.useCallback((group, index, qIndex, onGoToQuestion) => {
      return (
        <QuestionGroupList key={qIndex}>
          <SubText>{group.groupName}</SubText>
          <QuestionList>
            {group.questions.map((question) => {
              const { bottomBarQuestionNoes } = question;
              return bottomBarQuestionNoes?.map(({ no, answered }) => {
                return (
                  <DivQuestionBox
                    key={no}
                    className={cl({ active: answered })}
                    onClick={() => onGoToQuestion(`question-no-${no}`, index, qIndex)}
                  >
                    {no}
                  </DivQuestionBox>
                );
              });
            })}
          </QuestionList>
        </QuestionGroupList>
      );
    }, []);

    const renderQuestionSection = React.useCallback((testSection, index, onGoToQuestion) => {
      const combinedQuestions = [];
      testSection.questionGroups.forEach((questionGroup) => {
        combinedQuestions.push(...questionGroup.questions);
      });

      return (
        <QuestionGroupList key={index} className="section-questions">
          <Typography.Text variant="bold/10">{`SECTION ${index + 1}`}</Typography.Text>
          <QuestionList>
            {combinedQuestions.map((question) => {
              const { bottomBarQuestionNoes } = question;
              return bottomBarQuestionNoes?.map(({ no, answered }) => (
                <DivQuestionBox
                  key={no}
                  className={cl({ active: answered })}
                  onClick={() => onGoToQuestion(`question-no-${no}`, index)}
                >
                  {no}
                </DivQuestionBox>
              ));
            })}
          </QuestionList>
        </QuestionGroupList>
      );
    }, []);

    const renderTestSectionTabPanes = React.useCallback(
      (testSections, onGoToQuestion) => {
        let groupedTestSections = [];
        const testSectionPanes = [];
        testSections.forEach((testSection, index) => {
          groupedTestSections.push({ testSection, index });

          if (groupedTestSections.length === 2 || (index % 2 !== 0 && index === testSections - 1)) {
            testSectionPanes.push(
              <Tabs.TabPane
                tab={
                  <Typography.Text variant="semi-bold/16">{`Section ${
                    groupedTestSections.length === 1
                      ? index + 1
                      : index
                          .toString()
                          .concat('-')
                          .concat(index + 1)
                  }`}</Typography.Text>
                }
                key={index}
                closable={false}
              >
                {groupedTestSections.map((ts) => (
                  <div key={ts.index} className="test-section-wrapper">
                    {renderQuestionSection(ts.testSection, ts.index, onGoToQuestion)}
                  </div>
                ))}
              </Tabs.TabPane>
            );
            groupedTestSections = [];
          }
        });
        return testSectionPanes;
      },
      [renderQuestionSection]
    );

    return (
      <StyledDrawerModal
        height="auto"
        maxHeight="64vh"
        bodyStyle={noPaddingStyle}
        visible={visible}
        onClose={onClose}
        footer={false}
        closable={false}
        transitionOpacity={!store.tourGuide.isTourGuideFinished}
        title={
          <OverallTitle
            title={isFullTest ? 'Lưu ý' : 'Overall'}
            subTitle={
              isFullTest
                ? `Bạn có ${
                    store.reviewTime / 60000
                  } để review và sửa lại đáp án cho các câu hỏi ở các sections ${range(
                    1,
                    store.totalSections
                  ).join(' ')} và ${store.totalSections}}.`
                : `Total: ${store.totalQuestions} questions`
            }
            onClose={onClose}
          />
        }
      >
        <DrawerBody footer>
          <BodyWrapper>
            {isFullTest && (
              <Tabs hideAdd className="section-tabs" defaultActiveKey="0">
                {renderTestSectionTabPanes(store.testSections, goToQuestion)}
              </Tabs>
            )}
            {!isFullTest &&
              store.testSections.map((testSection, index) => {
                return (
                  <div key={index}>
                    {testSection.questionGroups.map((group, qIndex) =>
                      renderQuestionGroup(group, index, qIndex, goToQuestion)
                    )}
                  </div>
                );
              })}
          </BodyWrapper>
        </DrawerBody>
        <ButtonFooter>
          <Button.Primary onClick={onSubmit} className="btn-submit-do-test" style={{ height: 48 }}>
            Submit
          </Button.Primary>
        </ButtonFooter>
      </StyledDrawerModal>
    );
  }
);

const StyledDrawerModal = styled(DrawerModal)`
  .ant-tabs-nav {
    margin-top: -8px;
  }
`;
